import {
    Box,
    Button,
    FileUpload,
    FormField,
    Header,
    Icon,
    Input,
    NonCancelableCustomEvent,
    Popover,
    SpaceBetween,
    Spinner,
    TextContent,
    TokenGroup,
    TokenGroupProps,
} from '@amzn/awsui-components-react';
import { Order } from '@amzn/ito-client';
import { getString } from 'common';
import { useFlashBarItemsActions } from 'common/UseFlashBarItems/useFlashBarItems';
import { useAddOrderSublineItem, useAddOrderWatchers,useGetAttachmentsResponse, useRemoveOrderWatchers, useUploadFile } from 'hooks';
import { AMAZONIAN } from 'interfaces/constants';
import { debounce } from 'lodash';
import { useEffect, useState } from 'react';
import { CSRFService } from 'services/csrf-service';
import { useOrderEditActions, useOrderEditItems } from 'views/OrderDetailsSingleViewPage/useOrderEdit';

import { buildRenderedFormFields } from '../../../common/utils/form-utils';
import { sanitizeWatcher, validateWatcher } from '../../../helpers/data-sanitization';
import { hasAmazonianLabel } from '../../../helpers/helpers';
import { useUpdateOrder } from '../../ManageOrders/AssignOrderModal/useUpdateOrder';
import { CancelOrderButton } from './OrderSummaryTab/CancelOrderButton';
import { OrderAttachments } from './OrderSummaryTab/OrderAttachments';
import { UserPermissions } from '../../../services/user-permissions';
import { PERMISSIONS } from '../../../configuration/config';
import ordersFieldConfig from "./OrderSummaryTab/orderDetails.form-config.json";
import { ColumnFormSection } from './OrderSummaryTab/OrderDetailsSection';
import { EditableOrderDetails, useOrderSummaryForm } from './OrderSummaryTab/useOrderSummaryForm';
const stringPath = "orderDetails.tabs.orderSummary";

interface OrderSummaryProps {
    order: Order,
    amznhamOrder?: Order,
    useOrderEditItems: useOrderEditItems,
    useOrderEditActions: useOrderEditActions,
    flashBarItemsActions: useFlashBarItemsActions,
    attachmentsHook: useGetAttachmentsResponse,
    hideRequesterDetails?: boolean,
}

export const OrderSummaryTab = (props: OrderSummaryProps) => {
    const order = props.order;
    const items = props.useOrderEditItems;
    const isAmazonian = hasAmazonianLabel(order, AMAZONIAN);

    const {
        isLoading: isPostAttachmentLoading,
        response: postAttachmentResponse,
        error: postAttachmentError,
        doUploadFile
    } = useUploadFile();

    const {
        isLoading: isLoadingOrderStatus,
        data,
        error: errorOrderStatus,
        updateOrderSummary
    } = useUpdateOrder();

    const {
        response: addOrderWatchersResponse,
        isLoading: isLoadingAddOrderWatchers,
        error: errorAddOrderWatcher,
        doAddOrderWatchers
    } = useAddOrderWatchers();

    const {
        response: removeOrderWatchersResponse,
        isLoading: isLoadingRemoveOrderWatchers,
        error: errorRemoveOrderWatcher,
        doDeleteOrderWatchers
    } = useRemoveOrderWatchers();

    const { formValues, handleInputChange, getUpdatedFields, procurementMembers } = useOrderSummaryForm(order);
    const [hasValidationErrors, setHasValidationErrors] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File[]>([]);
    const [isEditMode, setEditMode] = useState(false);
    const enableCancelOrder = UserPermissions.instance.hasPermission(PERMISSIONS.SHOW_CANCEL_ORDER);

    const renderedFormFields: { [id: string]: JSX.Element } = buildRenderedFormFields<EditableOrderDetails>(
        ordersFieldConfig.fields,
        props.order,
        isEditMode,
        stringPath,
        formValues,
        handleInputChange,
        procurementMembers,
        setHasValidationErrors
    );

    // To trigger when data is updated
    useEffect(() => {
        if (data && !errorOrderStatus) {
            props.flashBarItemsActions.addSuccessFlashBarItem(`The order was updated correctly.`);
            props.useOrderEditActions.reloadOrder(order.orderId);
        }
        else if (errorOrderStatus) {
            props.flashBarItemsActions.addErrorFlashBarItem(`Error updating the order. ${errorOrderStatus.message}`);
        }
        setEditMode(false);
    }, [data, errorOrderStatus]);

    const onClickEdit = () => {
        setEditMode(true);
    }

    const onClickSubmit = () => {
        if (hasValidationErrors) {
            props.flashBarItemsActions.addErrorFlashBarItem("Please resolve all validation errors before submitting the order.");
            return;
        }
        const firstEditFlag = (props.useOrderEditItems.currentOrder?.details as any).firstEdit;

        const csrfService = new CSRFService();
        csrfService.getToken().then((token: string) => {
            updateOrderSummary({
                orders: [order],
                updateAssigneeProps: getUpdatedFields(formValues, firstEditFlag === undefined),
                csrfToken: token,
                updateOnAmznHam: false
            });
            updateOrderSummary({
                orders: [order],
                updateAssigneeProps: getUpdatedFields(formValues, firstEditFlag === undefined),
                csrfToken: token,
                updateOnAmznHam: true
            });
        });
    }

    const uploadFile = async () => {
        const token = await new CSRFService().getToken();
        for (let i = 0; i < selectedFile.length; i++) {
            doUploadFile(props.order.orderId!, selectedFile[i], selectedFile[i].type, selectedFile[i].name, token)
        }
        setSelectedFile([]);
    }
    const debouncedUploadFile = debounce(uploadFile, 500)
    const handleUploadFile = () => {
        debouncedUploadFile();
    }
    // To trigger when data is updated
    useEffect(() => {
        if (postAttachmentResponse) {
            props.attachmentsHook.doGetAttachments();
        }
        if (postAttachmentError) {
            props.flashBarItemsActions.addErrorFlashBarItem(postAttachmentError.message);
        }
    }, [postAttachmentResponse, postAttachmentError]);

    function renderAttachments() {
        if (props.attachmentsHook.isLoading) {
            return (<Spinner />)
        } else {
            return (
                <OrderAttachments
                    attachments={props.attachmentsHook.data}
                    error={props.attachmentsHook.error}
                />
            )
        }
    }

    useEffect(() => {
        if (addOrderWatchersResponse && !errorAddOrderWatcher) {
            // If adding watcher was successful, flag the order
            props.useOrderEditActions.flagOrderWithFirstEdit();
            props.useOrderEditActions.reloadOrder(props.order.orderId!);
            props.useOrderEditActions.setInputWatcher("");
            props.flashBarItemsActions.addSuccessFlashBarItem(getString(`${stringPath}.watcherDetails.success.watcherAdded`));
        } else if (errorAddOrderWatcher) {
            props.flashBarItemsActions.addErrorFlashBarItem(`${getString(`${stringPath}.watcherDetails.error.errorAddingWatcher`)} - ${errorAddOrderWatcher}`);
        }
    }, [addOrderWatchersResponse, errorAddOrderWatcher]);

    useEffect(() => {
        if (removeOrderWatchersResponse && !errorRemoveOrderWatcher) {
            // If adding watcher was successful, flag the order
            props.useOrderEditActions.flagOrderWithFirstEdit();
            props.useOrderEditActions.reloadOrder(props.order.orderId!);
            props.flashBarItemsActions.addSuccessFlashBarItem(getString(`${stringPath}.watcherDetails.success.watcherRemoved`));
        } else if (errorRemoveOrderWatcher) {
            props.flashBarItemsActions.addErrorFlashBarItem(`${getString(`${stringPath}.watcherDetails.error.errorRemovingWatcher`)} - ${errorRemoveOrderWatcher}`);
        }
    }, [removeOrderWatchersResponse, errorRemoveOrderWatcher]);

    async function addWatcher() {
        try {
            const csrfService = new CSRFService();
            const csrfToken = await csrfService.getToken();
            await doAddOrderWatchers(props.order.orderId!, sanitizeWatcher(props.useOrderEditItems.inputWatcher.toLowerCase()), csrfToken);
        } catch (error) {
            props.flashBarItemsActions.addErrorFlashBarItem(`${getString(`${stringPath}.watcherDetails.error.errorAddingWatcher`)} - ${error}`);
        }
    }

    async function removeWatcher (event: NonCancelableCustomEvent<TokenGroupProps.DismissDetail>) {
        try {
            const detail = event.detail;
            const csrfService = new CSRFService();
            const csrfToken = await csrfService.getToken();
            if (props.order.watchers) await doDeleteOrderWatchers(props.order.orderId!, Object.keys(props.order.watchers)[detail.itemIndex], csrfToken);
        } catch (error) {
            props.flashBarItemsActions.addErrorFlashBarItem(`${getString(`${stringPath}.watcherDetails.error.errorRemovingWatcher`)} - ${error}`);
        }
    }

    // Watcher validation logic
    const validateWatcherInput = (inputValue: string) => {
        // If truly empty (user hasn't typed anything), no error
        if (inputValue === "") {
            return { 
                isValid: false, 
                errorKey: `${stringPath}.watcherDetails.error.emptyWatcher`,
                errorMessage: ""
            };
        }
        
        const sanitized = sanitizeWatcher(inputValue);
        
        // If input had content but becomes empty after sanitization (whitespace only)
        if (!sanitized) {
            return { 
                isValid: false, 
                errorKey: `${stringPath}.watcherDetails.error.emptyWatcher`,
                errorMessage: getString(`${stringPath}.watcherDetails.error.emptyWatcher`)
            };
        }
        
        // Check if already exists
        if (props.order.watchers && Object.keys(props.order.watchers).includes(sanitized.toLowerCase())) {
            return { 
                isValid: false, 
                errorKey: `${stringPath}.watcherDetails.error.watcherAlreadyExists`,
                errorMessage: getString(`${stringPath}.watcherDetails.error.watcherAlreadyExists`)
            };
        }
        
        // Check if valid format (letters only)
        if (!validateWatcher(sanitized)) {
            return { 
                isValid: false, 
                errorKey: `${stringPath}.watcherDetails.error.nonValidWatcher`,
                errorMessage: getString(`${stringPath}.watcherDetails.error.nonValidWatcher`).replace('{{invalidWatchers}}', sanitized)
            };
        }
        
        return { isValid: true, errorMessage: "" };
    };

    // Get current validation state
    const watcherValidation = validateWatcherInput(props.useOrderEditItems.inputWatcher);

    return (
        <Box padding={{ "top": "m" }} >
            <SpaceBetween size="xxl">
                <SpaceBetween data-testid="order-details-section" size="m">
                    <Header data-testid="order-details-header" variant="h1"
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                {isEditMode && <Box float="right">
                                    <SpaceBetween size="xs" direction="horizontal">
                                        <Button
                                            data-testid="cancel-edit-button"
                                            disabled={!isEditMode || isLoadingOrderStatus}
                                            variant="normal"
                                            onClick={() => setEditMode(false)}>Cancel</Button>
                                        <Button
                                            data-testid="submit-edit-button"
                                            disabled={!isEditMode || isLoadingOrderStatus}
                                            variant="primary"
                                            loading={isLoadingOrderStatus}
                                            onClick={onClickSubmit}>Submit</Button>
                                    </SpaceBetween>
                                </Box>}
                                {
                                    items.canBeEdited &&
                                    !isEditMode &&
                                    <Button data-testid="edit-order-button" disabled={isEditMode} onClick={onClickEdit}>Edit</Button>}
                                {!isEditMode && enableCancelOrder && <CancelOrderButton
                                    order={order}
                                    amznhamOrder={props.amznhamOrder}
                                    flashBarItemsActions={props.flashBarItemsActions}
                                    onCancellationComplete={() => props.useOrderEditActions.reloadOrder(order.orderId)}
                                />}
                            </SpaceBetween>

                        }
                    >{getString(`${stringPath}.orderDetails`)}
                    </Header>
                    <ColumnFormSection
                        columnsWithRows={[
                            [
                                "orderID",
                                "orderStatus",
                                "assignee"
                            ],
                            [
                                "requestType",
                                "requestCategories",
                                "totalQuantity"
                            ],
                            [
                                "requestedDate",
                                "expedited",
                                "age"
                            ]
                        ]}
                        renderedFormFields={renderedFormFields}
                    />
                </SpaceBetween>

                {!props.hideRequesterDetails &&
                    <SpaceBetween data-testid="requester-details-section" size="m">
                        <Header data-testid="requester-details-header" variant="h1">{getString(`${stringPath}.requesterDetails`)}</Header>
                        <ColumnFormSection
                            columnsWithRows={[
                                [
                                    "name",
                                    "alias"
                                ],
                                [
                                    "country",
                                    "building"
                                ],
                                [
                                    "manager"
                                ]
                            ]}
                            renderedFormFields={renderedFormFields}
                        />
                    </SpaceBetween>}

                <SpaceBetween data-testid="shipping-details-section" size="m">
                    <Header
                        data-testid="shipping-details-header"
                        variant="h1"
                    >
                        {getString(`${stringPath}.shippingDetails`)}
                    </Header>
                    {isAmazonian ? (
                        <ColumnFormSection
                            columnsWithRows={[
                                ["amznhamDeliverAddressLineData", "amznhamDeliverCountry"],
                                ["amznhamDeliverCity", "amznhamDeliverPostalCode"],
                                ["amznhamDeliverState", "amznhamDeliverInstructions"]
                            ]}
                            renderedFormFields={renderedFormFields}
                        />
                    ) : (
                        <ColumnFormSection
                            columnsWithRows={[
                                ["deliverToBuilding", "deliverToRegion"],
                                ["deliverToCity"],
                                ["deliverToCountry"]
                            ]}
                            renderedFormFields={renderedFormFields}
                        />
                    )}
                </SpaceBetween>

                <SpaceBetween data-testid="financial-details-section" size="xs">
                    <Header data-testid="financial-details-header" variant="h1">{getString(`${stringPath}.financialDetails`)}</Header>
                    {isAmazonian ? (
                        <ColumnFormSection
                        columnsWithRows={[
                            [
                                "amznhamCostCenter",
                                "amznhamBusinessJustification",
                                "amznhamCarCode"
                            ],
                            [
                                "projectCode",
                                "financialCode"
                            ],
                            [
                                "companyGlCode",
                                "locationGlCode"
                            ]
                        ]}
                    renderedFormFields={renderedFormFields}
                    />
                    ) : (
                        <ColumnFormSection
                        columnsWithRows={[
                            [
                                "costCenter",
                                "carCode",
                                "businessJustification",
                            ],
                            [
                                "projectCode",
                                "financialCode"
                            ],
                            [
                                "companyGlCode",
                                "locationGlCode"
                            ]
                        ]}
                        renderedFormFields={renderedFormFields}
                    />
                    )}
                </SpaceBetween>

                <SpaceBetween data-testid="watchers-section" size="m">
                    <Header
                        data-testid="watchers-header"
                        variant="h1"
                    >
                        <SpaceBetween direction={"horizontal"} size={"xs"}>
                            {getString(`${stringPath}.watcherDetails.title`)}
                            {items.canBeEdited &&
                                <Popover
                                    dismissButton={false}
                                    position={"top"}
                                    wrapTriggerText={false}
                                    content={<TextContent>{getString(`${stringPath}.watcherDetails.description`)}</TextContent>}
                                >
                                    <Icon name={"status-info"} size={"small"} />
                                </Popover>
                            }
                        </SpaceBetween>
                    </Header>
                    {isEditMode && (
                        <SpaceBetween size="s" direction='horizontal'>
                            <FormField errorText={watcherValidation.errorMessage}>
                                <Input
                                    onChange={(event) => props.useOrderEditActions.setInputWatcher(event.detail.value)}
                                    value={props.useOrderEditItems.inputWatcher}
                                    invalid={!watcherValidation.isValid && watcherValidation.errorMessage !== ""}
                                    disabled={isLoadingAddOrderWatchers}
                                    data-testid="order-watcher-input"
                                    placeholder={getString(`${stringPath}.watcherDetails.inputPlaceholder`)}
                                />
                            </FormField>
                            <Button
                                variant="primary"
                                onClick={addWatcher}
                                loading={isLoadingAddOrderWatchers}
                                disabled={isLoadingAddOrderWatchers || !watcherValidation.isValid}
                                data-testid="order-add-watcher"
                            >
                                {getString(`${stringPath}.watcherDetails.sendButton`)}
                            </Button>
                        </SpaceBetween>
                    )}
                    {props.order.watchers && Object.keys(props.order.watchers).length > 0 ?
                        <TokenGroup
                            data-testid="watcher-list"
                            onDismiss={removeWatcher}
                            items={
                                props.order.watchers ?
                                Object.keys(props.order.watchers).map((watcher) => ({
                                    label: watcher,
                                    dismissLabel: "",
                                })) :
                                []
                            }
                            readOnly={!isEditMode}
                        />
                    :
                        <Box data-testid="no-watcher-message" color="text-status-inactive">{getString(`${stringPath}.watcherDetails.noWatchers`)}</Box>
                    }
                </SpaceBetween>

                <SpaceBetween data-testid="attachments-section" size="m">
                    <Header data-testid="attachments-header" variant="h1"
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    data-testid="upload-attachment-button"
                                    variant="primary"
                                    onClick={handleUploadFile}
                                    disabled={isPostAttachmentLoading || selectedFile.length < 1}
                                    loading={isPostAttachmentLoading}
                                >
                                    {getString(`${stringPath}.upload`)}
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        {getString(`${stringPath}.attachments`)}
                    </Header>
                    {renderAttachments()}
                    {items.canBeEdited && <FormField data-testid="upload-attachments-form">
                        <FileUpload
                            onChange={({ detail }) => setSelectedFile(detail.value)}
                            value={selectedFile}
                            i18nStrings={{
                                uploadButtonText: e => `Choose ${e ? "files" : "file"}`,
                                dropzoneText: e => `Drop ${e ? "files" : "file"} to upload`,
                                removeFileAriaLabel: e => `Remove file ${e + 1}`,
                                limitShowFewer: "Show fewer files",
                                limitShowMore: "Show more files",
                                errorIconAriaLabel: "Error"
                            }}
                            multiple
                            showFileLastModified
                            showFileSize
                            showFileThumbnail
                        />
                    </FormField>}
                </SpaceBetween>
            </SpaceBetween >
        </Box>
    );
}
